import { isNaN, isNil } from 'lodash';
import { memo } from 'react';
import styled from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import { formatCurrency, getCurrencyParts } from '../../utils/currency';
import type { TypographyProps } from '../typography/typography';
import Typography from '../typography/typography';

// Typography helpers
const { h1, h2, h3, bodyLg2, bodyMd1, bodyMd2, bodySm2 } = TypographyVariant;
const Span = (props: TypographyProps) => <Typography as="span" {...props} />;

const SIZE_MAP: Record<string, TypographyVariant> = {
  xxs: bodySm2,
  xs: bodyMd1,
  sm: bodyLg2,
};

export type CurrencySize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

export interface CurrencyProps {
  value?: number | string | null;
  currency?: string | null;
  showPlus?: boolean;
  size?: CurrencySize;
  color?: string;
  isEmpty?: boolean;
  as?: 'span';
  className?: string;
  'data-testid'?: string;
  isMasked?: boolean;
  showIsoCode?: boolean;
}

const Currency = ({
  value,
  currency,
  showPlus,
  size = 'xs',
  color,
  isEmpty,
  isMasked = false,
  showIsoCode = true,
  ...rest
}: CurrencyProps) => {
  if (isEmpty) {
    return (
      <Typography color={color} variant={bodyMd1} {...rest}>
        -
      </Typography>
    );
  }

  if (isNil(value) || isNaN(value)) {
    return false;
  }

  if (SIZE_MAP[size]) {
    return (
      <Typography color={color} variant={SIZE_MAP[size]} {...rest}>
        {formatCurrency(value, currency, { showPlus, isMasked, showIsoCode })}
      </Typography>
    );
  }

  const { sign, leftSymbol, integer, decimal, rightSymbol, isoCode } =
    getCurrencyParts(value, currency, { showPlus, isMasked, showIsoCode });

  if (size === 'md') {
    return (
      <Wrapper {...rest}>
        <Span color={color} variant={bodyMd2}>
          {sign}
          {leftSymbol}
        </Span>
        <Span color={color} variant={h1}>
          {integer}
        </Span>
        <Span color={color} variant={bodyMd2}>
          {decimal}
          {rightSymbol}
        </Span>
        {isoCode && (
          <Span color={color} variant={bodySm2}>
            {isoCode}
          </Span>
        )}
      </Wrapper>
    );
  }

  if (size === 'lg') {
    return (
      <Wrapper {...rest}>
        <Span color={color} variant={h2}>
          {sign}
          {leftSymbol}
        </Span>
        <LargeInteger color={color} variant={h1}>
          {integer}
        </LargeInteger>
        <Span color={color} variant={h2}>
          {decimal}
          {rightSymbol}
        </Span>
        {isoCode && (
          <Span color={color} variant={h3}>
            {isoCode}
          </Span>
        )}
      </Wrapper>
    );
  }

  return false;
};

export default memo(Currency);

const Wrapper = styled.p`
  margin: 0;
`;

const LargeInteger = styled(Span)`
  font-size: 2rem;
`;
